import { useEffect, useState } from 'react';
import { Badge, Card, Group, Loader, Spoiler, Stack, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import HttpClient from '../../helpers/HttpClient';
import { useLanguage } from '../../providers/LanguageContext';
import { useUserContext } from '../../providers/UserContext';
import { InboxMessage } from '../../types/message.type';

/** Current messages for the person (and, for parents, their children), shown in full. */
export function MessagesSection() {
  const { t } = useLanguage();
  const { currentPerson } = useUserContext();
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    new HttpClient<InboxMessage>('/message/inbox').getAll()
      .catch(() => [] as InboxMessage[])
      .then(setMessages)
      .finally(() => setLoading(false));
  }, [currentPerson]);

  return (
    <div data-tour="dashboard-messages">
      <Title order={3} mb="sm">{t.home.messages}</Title>
      {loading ? (
        <Loader size="sm" />
      ) : messages.length === 0 ? (
        <Text c="dimmed">{t.home.noMessages}</Text>
      ) : (
        <Stack gap="sm">
          {messages.map((msg) => (
            <Card key={msg.id} withBorder padding="md" radius="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap" mb={4}>
                <Text fw={600}>{msg.subject}</Text>
                {msg.forNames.length > 0 && (
                  <Badge variant="light" style={{ flexShrink: 0 }}>
                    für {msg.forNames.join(', ')}
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed" mb="xs">
                {msg.senderName || '—'} · {dayjs(msg.createdAt).format('DD.MM.YYYY')}
                {msg.validTo && ` · gültig bis ${dayjs(msg.validTo).format('DD.MM.YYYY')}`}
              </Text>
              <Spoiler maxHeight={140} showLabel="Mehr anzeigen" hideLabel="Weniger anzeigen">
                {/* Internal app — HTML from trusted admin writers */}
                <div dangerouslySetInnerHTML={{ __html: msg.body }} />
              </Spoiler>
            </Card>
          ))}
        </Stack>
      )}
    </div>
  );
}
